/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import { defineRecipe } from 'chakra-v3';

/**
 * Text Recipe - Typography system for consistent text styling
 *
 * Base: Most common text pattern (16px, DM Sans, normal, 1.4, 0.08px)
 * The 'body' fontFamily token maps to 'DM Sans', sans-serif
 * Used heavily across pages for body text, links, and general content.
 *
 * Usage:
 * ```tsx
 * <Text variant="body">Default body text</Text>
 * <Text variant="caption">Small caption text</Text>
 * <Text variant="link">Link text</Text>
 * ```
 */
export const textRecipe = defineRecipe({
  base: {
    fontFamily: 'body',
    fontSize: '16px',
    fontWeight: 'normal',
    lineHeight: '1.4',
    letterSpacing: '0.08px',
  },
  variants: {
    variant: {
      body: {
        // Base variant - same as base styles
        fontFamily: 'body',
        fontSize: '16px',
        fontWeight: 'normal',
        lineHeight: '1.4',
        letterSpacing: '0.08px',
      },
      caption: {
        // Small text for captions, footers, labels
        fontFamily: 'body',
        fontSize: '14px',
        fontWeight: 'normal',
        lineHeight: '1.406',
        letterSpacing: '0.08px',
      },
      link: {
        // Link styling - same as body but with underline
        fontFamily: 'body',
        fontSize: '16px',
        fontWeight: 'normal',
        lineHeight: '1.4',
        letterSpacing: '0.08px',
        textDecoration: 'underline',
      },
      label: {
        // For form labels and small headings
        fontFamily: 'body',
        fontSize: '14px',
        fontWeight: '500',
        lineHeight: '1.406',
        letterSpacing: '0.08px',
      },
      inputLabel: {
        // Input label styling per login design
        fontFamily: 'body',
        fontSize: '16px',
        fontWeight: '600',
        lineHeight: '1.4',
        letterSpacing: '0.08px',
        color: 'brand.beige',
      },
      heroHeading: {
        // Large hero heading on dashboard pages
        fontFamily: 'body',
        fontSize: '40px',
        fontWeight: '400',
        lineHeight: '140%',
        letterSpacing: '0.4px',
        textAlign: 'center',
        color: 'text.accent',
      },
      heroSubheading: {
        // Secondary message beneath hero heading
        fontFamily: 'subheading',
        fontSize: { base: '20px', md: '24px' },
        fontWeight: '500',
        lineHeight: '1.4',
        letterSpacing: '0.06px',
        color: 'text.footer',
      },
      heroEmptyState: {
        // Informational text when no projects are assigned
        fontFamily: 'subheading',
        fontSize: '20px',
        fontWeight: '500',
        lineHeight: '140%',
        letterSpacing: '0.05px',
        textAlign: 'center',
        color: 'text.footer',
      },
      projectIndex: {
        // Numeric index shown on each project card
        fontFamily: 'subheading',
        fontSize: '20px',
        fontWeight: '600',
        lineHeight: '1.4',
        letterSpacing: '0.1px',
        color: 'text.grey20',
      },
      projectTitle: {
        // Project name text on cards
        fontFamily: 'subheading',
        fontSize: '20px',
        fontWeight: '500',
        lineHeight: '1.4',
        letterSpacing: '0.05px',
        color: 'text.primary',
      },
      projectStatus: {
        // Status text on project cards
        fontFamily: 'subheading',
        fontSize: '16px',
        fontWeight: '600',
        lineHeight: '1.4',
        letterSpacing: '0.08px',
        color: 'text.grey20',
      },
      inherit: {
        // For text that should inherit parent styles (e.g., inside Headings)
        // No font styles applied - inherits from parent
        fontFamily: 'inherit',
        fontSize: 'inherit',
        fontWeight: 'inherit',
        lineHeight: 'inherit',
        letterSpacing: 'inherit',
      },
      platformText: {
        // Platform text component styling with extended line height
        fontFamily: 'body',
        fontSize: '16px',
        fontWeight: '600',
        lineHeight: '140%',
        letterSpacing: '0.08px',
        color: 'text.primary',
      },
      bodyBold: {
        // Body text with semibold weight
        fontFamily: 'body',
        fontSize: '16px',
        fontWeight: '600',
        lineHeight: '1.4',
        letterSpacing: '0.08px',
      },
      labelBold: {
        // Label text with bold weight
        fontFamily: 'body',
        fontSize: '14px',
        fontWeight: '700',
        lineHeight: '1.406',
        letterSpacing: '0.08px',
      },
      bodyExtended: {
        // Body text with extended line height for better readability
        fontFamily: 'body',
        fontSize: '16px',
        fontWeight: 'normal',
        lineHeight: '1.7',
        letterSpacing: '0.08px',
      },
      bodyGrey20: {
        // Body text with grey20 color
        fontFamily: 'body',
        fontSize: '16px',
        fontWeight: 'normal',
        lineHeight: '1.4',
        letterSpacing: '0.08px',
        color: 'text.grey20',
      },
      noteBody: {
        // Body text for note cards with extended line height for readability
        fontFamily: 'body',
        fontSize: '16px',
        fontWeight: 'normal',
        lineHeight: '1.6',
        letterSpacing: '0.08px',
        color: 'text.nearBlack',
      },
    },
    size: {
      sm: {
        fontSize: '14px',
        lineHeight: '1.406',
      },
      md: {
        fontSize: '16px',
        lineHeight: '1.4',
      },
      lg: {
        fontSize: '18px',
        lineHeight: '1.4',
      },
      xl: {
        fontSize: '20px',
        lineHeight: '1.4',
      },
    },
  },
  defaultVariants: {
    variant: 'body',
  },
});

